import { first } from 'rxjs/operators';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ListingQualityStatusService } from '@app/_components/listing-quality-status/listing-quality-status.service';
import { PropertyService } from '@app/_components/property/property.service';
import { Table } from 'primeng/table';

@Component({
    selector: 'app-quality-test-dashboard',
    templateUrl: './quality-test-dashboard.component.html',
    styleUrls: ['./quality-test-dashboard.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }
        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
    `],
    standalone: false
})
export class QualityTestDashboardComponent implements OnInit {

    @ViewChild('pt') pt: Table;

    constructor(
        private listingQualityStatusService: ListingQualityStatusService,
        private propertyService: PropertyService,
        private alertService: AlertService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private formBuilder: UntypedFormBuilder,
    ) {
        this.form1 = this.formBuilder.group({});
    }

    // -----------------------------------------------------------------------
    // Dashboard summary (aggregate)
    // -----------------------------------------------------------------------
    dashboard: any = null;
    dashboardLoading = false;

    // -----------------------------------------------------------------------
    // Raw results table
    // -----------------------------------------------------------------------
    listingQualityStatuss: any[] = [];
    listingQualityStatus: any;
    recordTotal = 0;
    loading = true;
    formLoading = false;
    lazyLoadEvent: LazyLoadEvent;
    multiSortMeta: any[] = [];
    first = 0;
    rows = 100;
    datasource: any;

    // -----------------------------------------------------------------------
    // Detail dialog
    // -----------------------------------------------------------------------
    selectedRow: any = null;
    showDetail = false;

    // -----------------------------------------------------------------------
    // Active filter state (drives visual highlights on KPI cards + category rows)
    // -----------------------------------------------------------------------
    activeStatusFilter: string = 'FAIL';
    activeCategoryFilter: string = null;

    // Tracks which parent category is currently running (for per-row spinner)
    refreshingCategory: string | null = null;

    // -----------------------------------------------------------------------
    // Filter dropdown models
    // -----------------------------------------------------------------------
    properties: any[] = [];
    testResultStatusValues: any[] = [];
    testNameValues: any[] = [];
    testCategoryValues: any[] = [];
    defaultTestResultStatus: any = 'FAIL';
    defaultTestResultStatusFlag = true;
    filterTestName: any = null;
    filterTestCategory: any = null;

    form1: UntypedFormGroup;
    formReady = false;

    columns: any[] = [
        { field: 'testResultStatus',  header: 'Status',      width: '90px'  },
        { field: 'shortTermRentalId', header: 'STR Id',      width: '70px'  },
        { field: 'testSubjectName',   header: 'Property',    width: '140px' },
        { field: 'testCategory',      header: 'Category',    width: '130px' },
        { field: 'testName',          header: 'Test Name',   width: '160px' },
        { field: 'testResultValue',   header: 'Result',      width: '140px' },
        { field: 'testExpectedValue', header: 'Expected',    width: '150px' },
        { field: 'sourceType',        header: 'Source',      width: '160px' },
        { field: 'testResultDesc',    header: 'Description', width: 'auto'  },
    ];

    ngOnInit(): void {
        this.formBuild();
        this.formReady = true;
        this.multiSortMeta = [{ field: 'createDate', order: -1 }];
        this.fetchProperties();
        this.fetchTestResultStatusValues();
        this.fetchTestNameUniqueValues();
        this.loadDashboard();
    }

    // -----------------------------------------------------------------------
    // Dashboard aggregate
    // -----------------------------------------------------------------------

    loadDashboard(): void {
        this.dashboardLoading = true;
        this.listingQualityStatusService.qualityTestDashboard()
            .pipe(first())
            .subscribe(
                (res: any) => {
                    this.dashboard = res.data?.dashboard || null;
                    this.dashboardLoading = false;
                    this.testCategoryValues = (this.dashboard?.byCategory || [])
                        .map((c: any) => ({ name: c.testCategory, value: c.testCategory }));
                },
                (err: any) => {
                    this.dashboardLoading = false;
                    console.log('dashboard load error:', err);
                }
            );
    }

    getCategoryHealthClass(score: number | null): string {
        if (score === null || score === undefined) return '';
        if (score >= 90) return 'health-good';
        if (score >= 70) return 'health-warn';
        return 'health-fail';
    }

    // -----------------------------------------------------------------------
    // Raw results lazy table
    // -----------------------------------------------------------------------

    loadLazy(event: LazyLoadEvent): void {
        try {
            this.loading = true;
            this.lazyLoadEvent = event;

            if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
                event.multiSortMeta = [{ field: 'createDate', order: -1 }];
            }

            if (this.defaultTestResultStatusFlag) {
                event.filters = event.filters || {};
                Object.assign(event.filters, { testResultStatus: { value: 'FAIL', matchMode: 'in' } });
                this.defaultTestResultStatusFlag = false;
            }

            const pageNumber = Math.round(event.first / event.rows);
            this.listingQualityStatusService.findByFilter(
                pageNumber, event.rows,
                event.sortField, event.sortOrder,
                event.multiSortMeta,
                event.globalFilter,
                event.filters
            ).pipe(first()).subscribe(
                (res: any) => {
                    this.datasource = res.data?.reviews;
                    this.recordTotal = res.page?.recordTotal || 0;
                    this.listingQualityStatuss = res.data?.qualityTests || [];
                },
                (err: any) => {
                    this.alertService.error(err);
                },
                () => { this.loading = false; }
            );

            if (this.datasource) {
                this.listingQualityStatuss = this.datasource.slice(event.first, event.first + event.rows);
            }
        } catch (error) {
            console.log('loadLazy error:', error);
        }
    }

    // -----------------------------------------------------------------------
    // Click filters — KPI cards and category rows drive table filter state
    // -----------------------------------------------------------------------

    filterByStatus(status: string): void {
        if (this.activeStatusFilter === status) {
            this.activeStatusFilter = null;
            this.defaultTestResultStatus = null;
            this.pt?.filter(null, 'testResultStatus', 'in');
        } else {
            this.activeStatusFilter = status;
            this.defaultTestResultStatus = status;
            this.pt?.filter(status, 'testResultStatus', 'in');
        }
    }

    filterByCategory(category: string): void {
        if (this.activeCategoryFilter === category) {
            this.activeCategoryFilter = null;
            this.filterTestCategory = null;
            this.pt?.filter(null, 'testCategory', 'in');
        } else {
            this.activeCategoryFilter = category;
            this.filterTestCategory = category;
            this.pt?.filter(category, 'testCategory', 'in');
        }
    }

    getActiveFilterLabels(): string[] {
        const labels: string[] = [];
        if (this.activeStatusFilter) { labels.push('Status: ' + this.activeStatusFilter); }
        if (this.activeCategoryFilter) { labels.push('Category: ' + this.activeCategoryFilter); }
        return labels;
    }

    // -----------------------------------------------------------------------
    // Detail dialog
    // -----------------------------------------------------------------------

    openDetail(row: any): void {
        this.selectedRow = row;
        this.showDetail = true;
    }

    closeDetail(): void {
        this.showDetail = false;
        this.selectedRow = null;
    }

    // -----------------------------------------------------------------------
    // Navigation links — open related records in new tabs
    // -----------------------------------------------------------------------

    openProperty(strId: number): void {
        if (!strId) { return; }
        window.open('/property-edit/' + strId, '_blank');
    }

    openSource(row: any): void {
        if (!row) { return; }
        if (row.sourceType === 'RESERVATION' && row.sourceId) {
            window.open('/reservations/' + row.sourceId, '_blank');
        } else if (row.sourceType === 'JOB' && row.sourceMask) {
            window.open('/logmans?referenceId=' + row.sourceMask, '_blank');
        } else if (row.sourceType === 'PROPERTY' && row.sourceId) {
            window.open('/property-edit/' + row.sourceId, '_blank');
        }
    }

    getSourceLabel(row: any): string {
        if (!row?.sourceType) { return ''; }
        const ref = row.sourceMask || row.sourceId;
        return ref ? row.sourceType + ' #' + ref : row.sourceType;
    }

    hasSourceLink(row: any): boolean {
        return row?.sourceType && (row.sourceId || row.sourceMask);
    }

    // -----------------------------------------------------------------------
    // Row delete
    // -----------------------------------------------------------------------

    onRowDelete(row: any): void {
        this.confirmationService.confirm({
            message: 'Delete result #' + row.qualityTestId + '?',
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => { this.formDelete(row); },
            reject: () => {
                this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
            }
        });
    }

    formDelete(row: any): void {
        this.formLoading = true;
        const deletedId = row.qualityTestId;
        this.listingQualityStatusService.delete(deletedId)
            .subscribe(
                (res: any) => {
                    if (this.showDetail && this.selectedRow?.qualityTestId === deletedId) {
                        this.closeDetail();
                    }
                    this.listingQualityStatuss.splice(this.listingQualityStatuss.indexOf(row), 1);
                    this.recordTotal = Math.max(0, this.recordTotal - 1);
                    this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Result #' + deletedId + ' removed.', life: 3000 });
                },
                (err: any) => {
                    this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: JSON.stringify(err), life: 3000 });
                },
                () => { this.formLoading = false; }
            );
    }

    getResStatusBg(status: string): string {
        if (status === 'FAIL') { return '#f8d7da'; }
        if (status === 'WARN') { return '#fff3cd'; }
        if (status === 'PASS') { return '#d4edda'; }
        if (status === 'INFO') { return '#d1ecf1'; }
        return '';
    }

    getResStatusColor(status: string): string {
        if (status === 'FAIL') { return '#721c24'; }
        if (status === 'WARN') { return '#856404'; }
        if (status === 'PASS') { return '#155724'; }
        if (status === 'INFO') { return '#0c5460'; }
        return '';
    }

    // -----------------------------------------------------------------------
    // Run quality test actions
    // -----------------------------------------------------------------------

    testQualityStatus(): void {
        const ids = this.getSelectedPropertyIds();
        if (!ids || ids.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Property Selected', detail: 'Select at least one property, or use Run All.', life: 4000 });
            return;
        }
        this.formLoading = true;
        this.listingQualityStatusService.testListingQualityStatus(ids)
            .subscribe(
                res => {
                    this.formLoading = false;
                    this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Listing quality tests complete.', life: 3000 });
                    this.loadLazy(this.lazyLoadEvent);
                    this.loadDashboard();
                },
                err => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: JSON.stringify(err), life: 3000 });
                }
            );
        this.messageService.add({ severity: 'info', summary: 'Running', detail: 'Test initiated. Results will refresh shortly.', life: 3000 });
    }

    testFeeAndTaxQuality(): void {
        const ids = this.getSelectedPropertyIds();
        if (!ids || ids.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Property Selected', detail: 'Select at least one property, or use Run All.', life: 4000 });
            return;
        }
        this.formLoading = true;
        this.listingQualityStatusService.testFeeAndTaxQualityStatus(ids)
            .subscribe(
                res => {
                    this.formLoading = false;
                    this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Fee & Tax quality tests complete.', life: 3000 });
                    this.loadLazy(this.lazyLoadEvent);
                    this.loadDashboard();
                },
                err => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: JSON.stringify(err), life: 3000 });
                }
            );
        this.messageService.add({ severity: 'info', summary: 'Running', detail: 'Test initiated. Results will refresh shortly.', life: 3000 });
    }

    testRateQuality(): void {
        const ids = this.getSelectedPropertyIds();
        if (!ids || ids.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Property Selected', detail: 'Select at least one property, or use Run All.', life: 4000 });
            return;
        }
        this.formLoading = true;
        this.listingQualityStatusService.testRateQualityStatus(ids)
            .subscribe(
                res => {
                    this.formLoading = false;
                    this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Rate quality tests complete.', life: 3000 });
                    this.loadLazy(this.lazyLoadEvent);
                    this.loadDashboard();
                },
                err => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: JSON.stringify(err), life: 3000 });
                }
            );
        this.messageService.add({ severity: 'info', summary: 'Running', detail: 'Test initiated. Results will refresh shortly.', life: 3000 });
    }

    testLedgerIntegrity(): void {
        this.formLoading = true;
        this.listingQualityStatusService.testLedgerIntegrityStatus()
            .subscribe(
                res => {
                    this.formLoading = false;
                    this.messageService.add({ severity: 'success', summary: 'Done', detail: 'Ledger integrity tests complete.', life: 3000 });
                    this.loadLazy(this.lazyLoadEvent);
                    this.loadDashboard();
                },
                err => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: JSON.stringify(err), life: 3000 });
                }
            );
        this.messageService.add({ severity: 'info', summary: 'Running', detail: 'Ledger integrity test initiated.', life: 3000 });
    }

    testAllListingQualityStatus(): void {
        this.formLoading = true;
        this.listingQualityStatusService.testAllListingQualityStatus()
            .subscribe(
                res => {
                    this.formLoading = false;
                    this.messageService.add({ severity: 'success', summary: 'Done', detail: 'All listing quality tests complete.', life: 3000 });
                    this.loadLazy(this.lazyLoadEvent);
                    this.loadDashboard();
                },
                err => {
                    this.alertService.error(err);
                }
            );
    }

    // -----------------------------------------------------------------------
    // Category-row Refresh — re-runs all tests for the parent category type
    // -----------------------------------------------------------------------

    /** Returns the top-level parent from a category string like "Listings/OTA" → "Listings" */
    getCategoryParent(category: string): string {
        if (!category) { return ''; }
        return category.split('/')[0].trim();
    }

    /** Human-readable label shown in the tooltip and toast messages */
    getRefreshParentLabel(category: string): string {
        const parent = this.getCategoryParent(category).toLowerCase();
        if (parent === 'listings' || parent === 'ru property status' || parent === 'features' || parent === 'review' || parent === 'ota') { return 'Listing'; }
        if (parent === 'pricing') { return 'Pricing (Rates + Fees)'; }
        if (parent === 'ledgerintegrity') { return 'Ledger Integrity'; }
        if (parent === 'operations') { return 'Operations'; }
        return this.getCategoryParent(category) || 'Tests';
    }

    /**
     * Re-runs all tests for the parent category of the clicked row.
     * If specific properties are selected in the picker, runs only for those.
     * If no properties selected, runs for all active properties.
     */
    refreshCategoryParent(category: string, event: Event): void {
        event.stopPropagation();
        const parent = this.getCategoryParent(category).toLowerCase();
        const ids = this.getSelectedPropertyIds();
        const label = this.getRefreshParentLabel(category);

        this.refreshingCategory = this.getCategoryParent(category);
        this.messageService.add({ severity: 'info', summary: 'Running', detail: 'Refreshing ' + label + ' tests...', life: 3000 });

        const onDone = () => {
            this.refreshingCategory = null;
            this.messageService.add({ severity: 'success', summary: 'Done', detail: label + ' tests complete.', life: 3000 });
            this.loadLazy(this.lazyLoadEvent);
            this.loadDashboard();
        };
        const onError = () => {
            this.refreshingCategory = null;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: label + ' test run failed.', life: 4000 });
        };

        switch (parent) {
            case 'listings':
            case 'ru property status':
            case 'features':
            case 'review':
            case 'ota': {
                const obs = ids.length > 0
                    ? this.listingQualityStatusService.testListingQualityStatus(ids)
                    : this.listingQualityStatusService.testAllListingQualityStatus();
                obs.pipe(first()).subscribe(onDone, onError);
                break;
            }
            case 'pricing': {
                if (ids.length > 0) {
                    // Run Rates, then Fee & Tax sequentially
                    this.listingQualityStatusService.testRateQualityStatus(ids).pipe(first()).subscribe(
                        () => this.listingQualityStatusService.testFeeAndTaxQualityStatus(ids).pipe(first()).subscribe(onDone, onError),
                        onError
                    );
                } else {
                    this.listingQualityStatusService.testAllListingQualityStatus().pipe(first()).subscribe(onDone, onError);
                }
                break;
            }
            case 'ledgerintegrity':
            case 'operations': {
                this.listingQualityStatusService.testLedgerIntegrityStatus().pipe(first()).subscribe(onDone, onError);
                break;
            }
            default: {
                const obs = ids.length > 0
                    ? this.listingQualityStatusService.testListingQualityStatus(ids)
                    : this.listingQualityStatusService.testAllListingQualityStatus();
                obs.pipe(first()).subscribe(onDone, onError);
            }
        }
    }

    reloadPage(): void {
        window.location.reload();
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    private getSelectedPropertyIds(): number[] {
        const selected = this.form1?.value?.shortTermRentals;
        if (!selected || selected.length === 0) { return []; }
        return selected.map((p: any) => p.shortTermRentalId);
    }

    fetchProperties(): void {
        this.propertyService.findAllSummary().subscribe(
            res => { this.properties = res.data?.properties || []; },
            err => { this.alertService.error(err); }
        );
    }

    fetchTestResultStatusValues(): void {
        this.listingQualityStatusService.findTestResultStatusValues().subscribe(
            res => {
                this.testResultStatusValues = (res.data?.testResultStatus || [])
                    .map((v: string) => ({ name: v, value: v }));
            },
            err => { this.alertService.error(err); }
        );
    }

    fetchTestNameUniqueValues(): void {
        this.listingQualityStatusService.findTestNameValues().subscribe(
            res => {
                this.testNameValues = (res.data?.testNames || [])
                    .map((v: string) => ({ name: v, value: v }));
            },
            err => { this.alertService.error(err); }
        );
    }

    formBuild(): void {
        this.form1 = this.formBuilder.group({ shortTermRentals: [] });
    }

    getTotalFails(byCategory: any[]): number {
        if (!byCategory) { return 0; }
        return byCategory.reduce((sum, c) => sum + (c.failCount || 0), 0);
    }

    getTotalWarns(byCategory: any[]): number {
        if (!byCategory) { return 0; }
        return byCategory.reduce((sum, c) => sum + (c.warnCount || 0), 0);
    }

    getTotalPasses(byCategory: any[]): number {
        if (!byCategory) { return 0; }
        return byCategory.reduce((sum, c) => sum + (c.passCount || 0), 0);
    }

    next()  { this.first = this.first + this.rows; }
    prev()  { this.first = this.first - this.rows; }
    reset() { this.first = 0; }
    isLastPage():  boolean { return this.listingQualityStatuss ? this.first === (this.listingQualityStatuss.length - this.rows) : true; }
    isFirstPage(): boolean { return this.listingQualityStatuss ? this.first === 0 : true; }
}
